import type { ContinuityLock, ContinuityPreparation, RenderArtifact, RenderJob, Plan, Project } from "@/types"

const API_BASE = "https://atlas-cloud.ogunwobiphilip.workers.dev"

function getToken(): string | null {
  return (window as unknown as Record<string, unknown>).__atlas_token__ as string | null
}

class ApiError extends Error {
  code: string
  status: number
  constructor(message: string, code: string, status: number) {
    super(message)
    this.code = code
    this.status = status
    this.name = "ApiError"
  }
}

export async function api(path: string, options?: RequestInit & { token?: string }): Promise<unknown> {
  const token = options?.token || getToken()
  if (!token) throw new Error("Sign in to Atlas first")
  const supplied = (options && (options.headers as Record<string, string>)) || {}
  const res = await fetch(API_BASE.replace(/\/$/, "") + path, {
    ...options,
    headers: { "Content-Type": "application/json", Authorization: "Bearer " + token, ...supplied },
  })
  const data = (await res.json().catch(() => ({}))) as Record<string, unknown>
  if (!res.ok) {
    const err = data?.error as Record<string, string> | undefined
    const message = err?.message || "Atlas Cloud returned " + res.status
    const code = err?.code || ""
    const error = new ApiError(message, code, res.status)
    if (code === "insufficient_credit" || code === "subscription_required") {
      window.dispatchEvent(new CustomEvent("atlas:open-credit-dialog", { detail: { message } }))
    }
    throw error
  }
  return data
}

export async function apiBlob(path: string, token?: string): Promise<Blob> {
  const t = token || getToken()
  if (!t) throw new Error("Sign in to Atlas first")
  const res = await fetch(API_BASE.replace(/\/$/, "") + path, { headers: { Authorization: "Bearer " + t } })
  if (!res.ok) {
    const data = (await res.json().catch(() => ({}))) as Record<string, unknown>
    const err = data?.error as Record<string, string> | undefined
    throw new Error(err?.message || "Atlas Cloud returned " + res.status)
  }
  return res.blob()
}

export async function downloadArtifact(artifactId: string, projectId: string, token?: string): Promise<string> {
  const data = await api(`/v1/projects/${projectId}/artifacts/${artifactId}/download`, { method: "POST", token }) as { downloadUrl: string; expiresAt: string }
  return data.downloadUrl
}

export function usd(micros: number): string {
  return "$" + ((Number(micros) || 0) / 1000000).toFixed(2)
}

export async function createProject(concept: string, title: string, token: string) {
  return api("/v1/projects", { method: "POST", body: JSON.stringify({ concept, title }), token }) as Promise<{ project: { id: string; title: string; concept: string; createdAt: string } }>
}

export async function expandProject(projectId: string, preferences: Record<string, string>, token: string) {
  return api(`/v1/projects/${projectId}/expand`, { method: "POST", body: JSON.stringify({ preferences }), token }) as Promise<{ revision: { content: { directions: unknown[] } } }>
}

export async function developProject(projectId: string, directions: unknown[], preferences: Record<string, string>, token: string) {
  return api(`/v1/projects/${projectId}/develop`, { method: "POST", body: JSON.stringify({ directions, preferences }), token }) as Promise<{ revision: { content: unknown } }>
}

export async function saveDocument(projectId: string, kind: string, content: unknown, token: string) {
  return api(`/v1/projects/${projectId}/documents/${kind}`, { method: "POST", body: JSON.stringify({ content }), token }) as Promise<{ revision: { id: string; hash: string; content: unknown } }>
}

export async function approveProject(projectId: string, subjectKind: string, subjectHash: string, token: string) {
  return api(`/v1/projects/${projectId}/approvals`, { method: "POST", body: JSON.stringify({ subjectKind, subjectHash, decision: "approved" }), token })
}

export async function startRender(projectId: string, body: Record<string, unknown>, token: string) {
  return api(`/v1/projects/${projectId}/renders`, { method: "POST", headers: { "Idempotency-Key": crypto.randomUUID() }, body: JSON.stringify(body), token }) as Promise<{ job: RenderJob }>
}

export async function getRenderStatus(projectId: string, jobId: string, token: string) {
  return api(`/v1/projects/${projectId}/renders/${jobId}`, { token }) as Promise<{ job: RenderJob; artifact: RenderArtifact | null }>
}

export async function cancelRender(projectId: string, jobId: string, token: string) {
  return api(`/v1/projects/${projectId}/renders/${jobId}/cancel`, { method: "POST", token })
}

export async function getLatestDailies(projectId: string, token: string) {
  return api(`/v1/projects/${projectId}/renders/latest-dailies`, { token }) as Promise<{ job: RenderJob | null; artifact: RenderArtifact | null }>
}

export async function getLatestMaster(projectId: string, token: string) {
  return api(`/v1/projects/${projectId}/renders/latest-master`, { token }) as Promise<{ job: RenderJob | null; artifact: RenderArtifact | null }>
}

export async function listProjectMasters(projectId: string, token: string) {
  return api(`/v1/projects/${projectId}/masters`, { token }) as Promise<{ masters: { jobId: string; capability: string; artifact: RenderArtifact; createdAt: string }[] }>
}

export async function publishProject(projectId: string, body: Record<string, unknown>, token: string) {
  return api(`/v1/projects/${projectId}/publications`, { method: "POST", body: JSON.stringify(body), token })
}

export async function getSession(token: string) {
  return api("/v1/session", { token }) as Promise<{
    user: { id: string; email: string; founder?: boolean }
    account: { creditMicros: number }
    billing: { plan: string; subscription?: boolean } | null
    capabilities: { creative: boolean; rendering: boolean; videoGeneration: boolean; finishing: boolean; youtube: boolean }
    integrations: { youtube: { connected: boolean; accountId: string | null } }
  }>
}

export async function listPlans(token: string) {
  return api("/v1/billing/plans", { token }) as Promise<{ plans: Plan[]; checkoutConfigured: boolean }>
}

export async function startCheckout(sku: string, token: string) {
  return api("/v1/billing/checkout", { method: "POST", body: JSON.stringify({ sku }), token }) as Promise<{ checkoutUrl: string }>
}

export async function listProjects(token: string) {
  return api("/v1/projects", { token }) as Promise<{ projects: Project[] }>
}

export async function getProject(projectId: string, token: string) {
  return api(`/v1/projects/${projectId}`, { token }) as Promise<{
    project: Project
    documents: { id: string; kind: string; revision: number; content_json: string; content_hash: string; created_at: string }[]
    approvals: { subject_kind: string; subject_hash: string; decision: string; created_at: string }[]
    latestBatch: { id: string; status: "pending" | "running" | "completed" | "failed" | "cancelled"; total_shots: number; completed_shots: number; failed_shots: number } | null
    latestAssembly: { job: RenderJob; artifact: RenderArtifact | null } | null
    latestContinuityPreparation: ContinuityPreparation | null
  }>
}

export async function prepareContinuity(projectId: string, shots: { sceneIndex: number; shotIndex: number; subject: string; camera: string; duration: number }[], continuity: string, token: string) {
  return api(`/v1/projects/${projectId}/continuity/prepare`, { method: "POST", headers: { "Idempotency-Key": crypto.randomUUID() }, body: JSON.stringify({ shots, continuity }), token }) as Promise<{ preparation: ContinuityPreparation }>
}

export async function getContinuityPreparation(projectId: string, preparationId: string, token: string) {
  return api(`/v1/projects/${projectId}/continuity/preparations/${preparationId}`, { token }) as Promise<{ preparation: ContinuityPreparation; continuity: ContinuityLock | null }>
}

export async function createBatch(projectId: string, shots: { sceneIndex: number; shotIndex: number; subject: string; camera: string; duration: number }[], continuityRevisionId: string, continuityHash: string, estimateMicros: number, authorizedMicros: number, profileId: string, token: string) {
  return api(`/v1/projects/${projectId}/batches`, { method: "POST", body: JSON.stringify({ shots, continuityRevisionId, continuityHash, estimateMicros, authorizedMicros, profileId }), token }) as Promise<{ batch: { id: string; shotIds: string[]; status: string }; dispatchErrors?: string[] }>
}

export async function getBatchProgress(projectId: string, batchId: string, token: string) {
  return api(`/v1/projects/${projectId}/batches/${batchId}/progress`, { token }) as Promise<{
    progress: { completed: number; failed: number; remaining: number; percent: number; etaSeconds: number | null; etaUpperBoundSeconds: number | null }
    shots: { id: string; sceneIndex: number; shotIndex: number; subject: string; duration: number; status: string; renderJobId: string | null; outputKey: string | null; bytes: number | null; artifactId: string | null; providerState: string | null; providerProgress: number | null; takeCount?: number; terminalTakeCount?: number; selectedTakeId?: string | null; qualityState?: string | null }[]
  }>
}

export async function cancelBatch(projectId: string, batchId: string, token: string) {
  return api(`/v1/projects/${projectId}/batches/${batchId}/cancel`, { method: "POST", token })
}

export async function startAssembly(projectId: string, batchRunId: string, shotKeys: string[], estimateMicros: number, authorizedMicros: number, subjectHash: string, profileId: string, token: string) {
  return api(`/v1/projects/${projectId}/renders`, { method: "POST", headers: { "Idempotency-Key": crypto.randomUUID() }, body: JSON.stringify({ capability: "video.assemble@1", input: { batchRunId, shots: shotKeys }, estimateMicros, authorizedMicros, subjectHash, profileId }), token }) as Promise<{ job: RenderJob }>
}

export { ApiError }
